import { service } from '@ember/service'
import Component from '@glimmer/component'
import { isHTMLSafe, htmlSafe } from '@ember/template'
import { colors } from '@colors'
import { tracked } from '@glimmer/tracking'
import { DataProcessor } from '@blakeelearning/data-tables/utils/data-processor'
import { t, type IntlService } from 'ember-intl'
import { ChartInfoTooltip } from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { SortColumnIcon } from 'teacher-ui-client/components/sort-column-icon'
import { optional } from 'teacher-ui-client/helpers/optional'
import { gradeName } from 'teacher-ui-client/helpers/grade-name'
import { formatValue } from 'teacher-ui-client/helpers/format-value'
import { maxBy, differenceWith } from 'lodash'
import { BaseHighcharterComponent } from 'teacher-ui-client/components/base/highcharter'
import type RouterService from '@ember/routing/router-service'
import type Student from 'teacher-ui-client/models/student'
import { Legend, TD, TH, type LegendsItem } from 'teacher-ui-client/components/giraffe/primitives/history-tables'
import type Owner from '@ember/owner'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface HistoryDataViewModel {
  firstName: string
  lastName: string
  teacherName: string
  gradePosition: number
  studentId: string
  min: Nullable<number>
  max: Nullable<number>
  initial: Nullable<number>
  growth: Nullable<number>
}

export type LexileDetailsHistoryDataItem = {
  student_id: number
  min: number
  max: number
  initial: number
  growth: number
}

interface Signature {
  Element: HTMLDivElement
  Args: {
    lexileDetailsData: LexileDetailsHistoryDataItem[]
    students: Student[]
    product: string
    subco: boolean
    tooltip: string
    uiScope: string
  }
}

type CategoryKey = 'initial' | 'growth'

const categories: CategoryKey[] = ['growth', 'initial']

interface ChartArgs {
  data: HistoryDataViewModel
  showYAxisLabels: boolean
  maxValue: number
}

const buildLexileChartOptions = function (chartOptions: ChartArgs): Highcharts.Options {
  const yAxis: Highcharts.YAxisOptions = {
    title: {
      text: '',
    },
    opposite: true,
    max: chartOptions.maxValue,
    minorTicks: false,
    labels: {
      enabled: chartOptions.showYAxisLabels,
    },
    offset: -10,
  }

  const series = categories.map((category) => {
    return {
      type: 'bar' as const,
      name: category,
      data: [chartOptions.data[category] ?? '0'],
    }
  })

  return {
    series,
    chart: {
      type: 'bar',
      // Make some room for the yaxis labels
      margin: chartOptions.showYAxisLabels ? [20, 0, 0, 0] : [0, 0, 0, 0],
      plotBackgroundColor: colors.dustyBlack50,
      // always disable animation for this chart, as they're all separate charts and don't animate together
      animation: false,
    },
    tooltip: {
      enabled: true,
      formatter: function (this: Highcharts.Point) {
        const initialCategoryId = 'initial'
        if (this.series.name === initialCategoryId) {
          return `<b>${this.y}L`
        }
        return `<b>+${this.y} growth`
      },
      backgroundColor: colors.dustyBlack500,
      borderColor: colors.dustyBlack500,
      style: {
        color: colors.white,
      },
    },
    colors: [colors.grapeyGreen300, colors.oceanyBlue300],
    credits: {
      enabled: false,
    },
    title: {
      text: '',
    },
    xAxis: {
      maxPadding: 0,
      minPadding: 0,
      labels: {
        enabled: false,
      },
      lineWidth: 0,
      tickWidth: 0,
      gridLineWidth: 0,
      minorGridLineWidth: 0,
    },
    legend: { enabled: false },
    yAxis,
    plotOptions: {
      bar: {
        animation: false,
        borderWidth: 0,
        colorByPoint: false,
        stacking: 'normal',
        pointWidth: 40,
      },
    },
  }
}

export class LexileDetailsHistory extends Component<Signature> {
  @tracked sortingPriorityQueue = []

  @service router!: RouterService

  @service intl!: IntlService

  @tracked processor: DataProcessor<HistoryDataViewModel>

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args)
    this.processor = new DataProcessor(this)
  }

  transitionToStudentRoute = (studentId: string) => {
    if (this.args.product === 'rex')
      return void this.router.transitionTo('reporting.ui-scope.rex.student-summary', studentId)
    return void this.router.transitionTo('reporting.ui-scope.re.student-summary', studentId)
  }

  get data() {
    return this.tableRows
  }

  get tableRows() {
    return this.args.students.filter(hasId).map((student) => {
      const statRow = this.args.lexileDetailsData?.find((stat) => String(stat.student_id) === student.id)

      return {
        firstName: student.firstName,
        lastName: student.lastName,
        teacherName: student.readingTeacherNames.join(', '),
        gradePosition: student.gradePosition,
        studentId: student.id,
        min: statRow?.min ?? null,
        max: statRow?.max ?? null,
        initial: statRow?.initial ?? null,
        growth: statRow?.growth ?? null,
      }
    })
  }

  get amendedTooltip() {
    const { tooltip } = this.args
    const missingStudentsCount = differenceWith(
      this.args.students,
      this.args.lexileDetailsData ?? [],
      (student, historyStudent) => student.id === String(historyStudent.student_id),
    ).length

    if (missingStudentsCount) {
      const wasSafe = isHTMLSafe(tooltip)
      const newTooltip = tooltip + this.intl.t('reporting.missingStudentsCount', { missingStudentsCount })
      return wasSafe ? htmlSafe(newTooltip) : newTooltip
    }
    return tooltip
  }

  get rowsWithChartOptions() {
    return this.processor.transformedData.map((row, index) => {
      return {
        ...row,
        chartOptions: buildLexileChartOptions({
          data: row,
          showYAxisLabels: index === 0,
          maxValue: this.maxLexile,
        }),
      }
    })
  }

  get maxLexile() {
    const max = maxBy(this.data, 'max')?.max ?? 450
    return max + 50 // give a little extra space on the chart
  }

  get legendCategories(): LegendsItem[] {
    return [
      { label: this.intl.t('reporting.charts.lexileDetails.history.categories.initial'), color: 'bg-oceany-blue-300' },
      { label: this.intl.t('reporting.charts.lexileDetails.history.categories.growth'), color: 'bg-grapey-green-300' },
    ]
  }

  get showChart() {
    return this.args.uiScope !== 'school'
  }

  <template>
    <div
      data-test-giraffe-lexile-details-history
      class="border-dusty-black-50 shadow-dusty-black-100 relative rounded-sm border shadow-md"
      ...attributes
    >
      <ChartInfoTooltip data-test-lexile-details-tooltip @tooltipContent={{this.amendedTooltip}} />
      <div class="overflow-x-auto print:block">
        <table data-test-history-table-lexile-details class="w-full min-w-6xl overflow-hidden rounded-sm text-sm">
          <thead>
            <tr class="bg-oceany-blue-300 divide-x divide-white font-semibold text-white">
              <TH class="w-36" {{on "click" (fn this.processor.tableActions.sortByValue "firstName")}}>
                <div class="flex items-center justify-between align-middle">
                  {{t "reporting.dashboardTables.firstName" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="firstName" />
                </div>
              </TH>
              <TH class="w-36" {{on "click" (fn this.processor.tableActions.sortByValue "lastName")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.lastName" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="lastName" />
                </div>
              </TH>
              {{#if @subco}}
                <TH class="w-48" {{on "click" (fn this.processor.tableActions.sortByValue "teacherName")}}>
                  <div class="flex items-center justify-between">
                    {{t "reporting.dashboardTables.teacher"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="teacherName" />
                  </div>
                </TH>
                <TH class="w-24" {{on "click" (fn this.processor.tableActions.sortByNumber "gradePosition")}}>
                  <div class="flex items-center justify-between">
                    {{t "grade.label"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="gradePosition" />
                  </div>
                </TH>
              {{/if}}
              <TH class="w-24" {{on "click" (fn this.processor.tableActions.sortByNumber "max")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.charts.lexileDetails.currentLexile" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="max" />
                </div>
              </TH>
              <TH class="w-24" {{on "click" (fn this.processor.tableActions.sortByNumber "growth")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.charts.lexileDetails.lexileGrowth" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="growth" />
                </div>
              </TH>
              {{#if this.showChart}}
                <TH
                  class="{{if @subco 'w-1/3' 'w-1/2'}}
                    border-none bg-white px-0! whitespace-nowrap text-black print:hidden"
                >
                  <Legend
                    @title={{t "reporting.chartTitles.lexileDetailsHistory"}}
                    @categories={{this.legendCategories}}
                  />
                </TH>
              {{/if}}
            </tr>
          </thead>
          <tbody class="divide-dusty-black-100 divide-y-2 print:divide-black">
            {{#each this.rowsWithChartOptions as |row index|}}
              {{! template-lint-disable no-invalid-interactive }}
              <tr
                data-test-lexile-details-student-row={{index}}
                class="cursor-pointer"
                {{on "click" (fn (optional this.transitionToStudentRoute) row.studentId)}}
              >
                <TD class="text-oceany-blue-300">
                  {{row.firstName}}
                </TD>
                <TD class="text-oceany-blue-300">
                  {{row.lastName}}
                </TD>
                {{#if @subco}}
                  <TD>
                    <div class="{{if this.showChart 'max-h-20 overflow-auto'}}">{{row.teacherName}}</div>
                  </TD>
                  <TD class="text-center">
                    {{gradeName row.gradePosition}}
                  </TD>
                {{/if}}
                <TD class="text-center">
                  {{formatValue row.max}}
                </TD>
                <TD data-test-growth class="text-center">
                  {{formatValue row.growth}}
                </TD>
                {{#if this.showChart}}
                  <TD class="border-dusty-black-100 cursor-default border-l px-0! py-0! print:hidden">
                    <BaseHighcharterComponent
                      data-test-lexile-details-highchart-row
                      class="h-full"
                      @options={{row.chartOptions}}
                    />
                  </TD>
                {{/if}}
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
    </div>
  </template>
}

export default LexileDetailsHistory
